import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { inr } from "@/lib/api";

const PILL = {
  needs_review: ["Needs review", "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30"],
  classified: ["Awaiting approval", "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30"],
  approved: ["Approved", "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"],
  suspense: ["Suspense", "bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30"],
  duplicate: ["Duplicate", "bg-zinc-500/15 text-zinc-500 border-zinc-500/30"],
  verified: ["Verified", "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"],
  failed: ["Review required", "bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30"],
  processing: ["Processing", "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30"],
  queued: ["Queued", "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30"],
  needs_account: ["Choose account", "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30"],
  password_required: ["Password needed", "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30"],
  error: ["Error", "bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30"],
  new: ["New", "bg-teal-500/10 text-teal-600 dark:text-teal-400 border-teal-500/30"],
  possible_duplicate: ["Possible duplicate", "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30"],
  confirmed_duplicate: ["Confirmed duplicate", "bg-zinc-500/15 text-zinc-500 border-zinc-500/30"],
  already_imported: ["Already imported", "bg-zinc-500/15 text-zinc-500 border-zinc-500/30"],
  none: ["Not exported", "bg-transparent text-muted-foreground border-border"],
  no_statements: ["No statements yet", "bg-transparent text-muted-foreground border-border"],
  exported: ["Exported (not yet confirmed)", "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30"],
  imported: ["Imported into Tally", "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"],
  import_failed: ["Import failed", "bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30"],
  pass: ["Pass", "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"],
  fail: ["Fail", "bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30"],
  warn: ["Warning", "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30"],
};

export const Pill = ({ s, testid }) => {
  const [label, cls] = PILL[s] || [s, "bg-muted text-muted-foreground"];
  return <Badge variant="outline" data-testid={testid} className={`whitespace-nowrap rounded-full px-2 py-0 text-[11px] font-medium ${cls}`}>{label}</Badge>;
};

export const Money = ({ v, dir, className = "" }) => (
  <span className={`num ${dir === "credit" ? "text-emerald-600 dark:text-emerald-400" : dir === "debit" ? "text-rose-600 dark:text-rose-400" : ""} ${className}`}>
    {inr(v)}
  </span>
);

export const PageHeader = ({ title, sub, children }) => (
  <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between fade-up">
    <div>
      <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="page-title">{title}</h1>
      {sub && <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{sub}</p>}
    </div>
    <div className="flex flex-wrap items-center gap-2">{children}</div>
  </div>
);

export const Kpi = ({ label, value, hint, icon: Icon, tone = "primary", testid, onClick }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className="panel group relative overflow-hidden p-5 text-left transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-primary/50">
    <div className="flex items-start justify-between">
      <span className="label-xs">{label}</span>
      {Icon && <Icon className={`h-4 w-4 ${tone === "rose" ? "text-rose-500" : tone === "amber" ? "text-amber-500" : tone === "emerald" ? "text-emerald-500" : tone === "teal" ? "text-teal-500" : "text-primary"}`} />}
    </div>
    <div className="num mt-3 text-2xl font-semibold tracking-tight">{value}</div>
    {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
  </button>
);

export const ReconBanner = ({ verified, testid = "reconciliation-status-banner", detail }) =>
  verified ? (
    <div data-testid={testid} className="flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-5 py-4 text-emerald-700 dark:text-emerald-300">
      <CheckCircle2 className="h-5 w-5 shrink-0" />
      <div><div className="font-display text-sm font-bold tracking-wide">ALL MANDATORY CHECKS PASSED</div>{detail && <div className="text-xs opacity-80">{detail}</div>}</div>
    </div>
  ) : (
    <div data-testid={testid} className="flex items-center gap-3 rounded-xl border border-rose-500/40 bg-rose-500/10 px-5 py-4 text-rose-700 dark:text-rose-300">
      <AlertTriangle className="h-5 w-5 shrink-0" />
      <div><div className="font-display text-sm font-bold tracking-wide">RECONCILIATION FAILED — REVIEW REQUIRED</div>{detail && <div className="text-xs opacity-80">{detail}</div>}</div>
    </div>
  );

export const Empty = ({ title, sub, children }) => (
  <div className="panel grid-bg flex flex-col items-start gap-2 p-10" data-testid="empty-state">
    <div className="font-display text-lg font-semibold">{title}</div>
    {sub && <div className="max-w-lg text-sm text-muted-foreground">{sub}</div>}
    {children}
  </div>
);
